import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, Observable } from 'rxjs';

const STORAGE_KEY = 'spark_app_version';

/**
 * Tracks app version and update availability from Angular Service Worker.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private version: string | null = null;
  private latestVersion: string | null = null;
  private isInitialized = false;
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);

  constructor(private swUpdate: SwUpdate) {
    this.version = localStorage.getItem(STORAGE_KEY);
  }

  initialize(): void {
    if (this.isInitialized || !this.swUpdate.isEnabled) {
      return;
    }
    this.isInitialized = true;

    this.swUpdate.versionUpdates.subscribe((evt) => {
      if (evt.type === 'NO_NEW_VERSION_DETECTED' && 'version' in evt) {
        this.setVersion(evt.version.hash);
      }
      if (evt.type === 'VERSION_READY' && 'currentVersion' in evt) {
        this.setVersion(evt.currentVersion.hash);
        this.latestVersion = evt.latestVersion.hash;
        this.updateAvailableSubject.next(true);
      }
    });
  }

  private setVersion(hash: string): void {
    this.version = hash;
    try {
      localStorage.setItem(STORAGE_KEY, hash);
    } catch (_) {}
  }

  /**
   * Returns the app version from SW (or from localStorage if we got it earlier).
   * Returns null if never received from SW (no fallback).
   */
  getVersionRaw(): string | null {
    return this.version;
  }

  getLatestVersionRaw(): string | null {
    return this.latestVersion;
  }

  updateAvailable$(): Observable<boolean> {
    return this.updateAvailableSubject.asObservable();
  }

  async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }
    return this.swUpdate.checkForUpdate();
  }

  async applyUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }
    const activated = await this.swUpdate.activateUpdate();
    if (activated) {
      window.location.reload();
    }
    return activated;
  }
}
